import { modalName, websiteFlagKeys } from '@va/constants';
import { openModal } from '@va/dashboard/actions/ui';
import { getWebsite } from '@va/dashboard/selectors/core';
import { modalName as saModalName } from '@va/standalone/shared/constants';
import { WebsiteResponse } from '@va/types/website';
import dayjs from 'dayjs';
import { put, select, take } from 'redux-saga/effects';

import {
  AccountDataType,
  dummyAnnouncements,
  getAnnouncementsToBeDisplayed,
  isDevelopFeatureEnabled,
} from '@va/util/helpers';

import { apiStatus, flagKeys, PLATFORM_IDS } from '@va/constants';
import { WixPermissionPack } from '@va/wix/shared/constants';
import { getPermissionPackage } from '@va/wix/shared/selectors';

import { setAnnouncementData } from '@va/dashboard/actions/ui';
import { getAnnouncementsSelector, getRequestStatus } from '@va/dashboard/selectors/api';
import { AnnouncementType } from '@va/types/announcements';

const FIRST_SHORT_BUSINESS_INFO_MODAL_INTERVAL_DAYS = 7;

export function* handleShortBusinessInfoModal() {
  const website: WebsiteResponse = yield select(getWebsite);
  const doNotShowAgain = website.frontendFlags?.[websiteFlagKeys.DO_NOT_SHOW_BUSINESS_INFO_MODAL];
  const isInfoComplete = !!(
    website.companyName &&
    website.businessSector &&
    website.businessType &&
    website.businessSize
  );

  const isOpen =
    !isInfoComplete &&
    !doNotShowAgain &&
    dayjs().diff(dayjs.unix(website.installDate), 'days') >= FIRST_SHORT_BUSINESS_INFO_MODAL_INTERVAL_DAYS;

  if (isOpen) {
    yield put(openModal(saModalName.SHORT_BUSINESS_INFO));
  }
}

export function* handleAnnouncementModalSaga() {
  const website: WebsiteResponse = yield select(getWebsite);
  const wixPermissionPack: WixPermissionPack = yield select(getPermissionPackage);
  const hasPremiumPack = wixPermissionPack > WixPermissionPack.FREE;
  const requestStatus: apiStatus = yield select((state) => getRequestStatus(state, 'getAnnouncements'));

  if (requestStatus === apiStatus.IN_PROGRESS) {
    yield take('GET_ANNOUNCEMENTS_SUCCEEDED');
  }

  const _announcements1 = dummyAnnouncements; // For manual testing, can be left here for now
  const announcements: AnnouncementType[] = yield select(getAnnouncementsSelector) ?? [];

  if (!website || !announcements) return;

  const { numberOfMonthlyVisits, installDate, ipCountryISO, frontendFlags } = website ?? {};

  const lastAccountBasedModalViewedAt =
    (frontendFlags?.[flagKeys.LAST_ACCOUNT_BASED_ANNOUNCEMENT_VIEWED_AT] as number) ?? 0;
  const lastWebsiteBasedModalViewedAt =
    (frontendFlags?.[flagKeys.LAST_WEBSITE_BASED_ANNOUNCEMENT_VIEWED_AT] as number) ?? 0;

  const websiteData = {
    numberOfMonthlyVisits,
    ipCountryISO,
    installDate,
    isWebsiteOwner: true,
    platform: PLATFORM_IDS.WIX,
    hasPremiumSubscription: hasPremiumPack,
    lastWebsiteBasedModalViewedAt,
  };

  const accountData: AccountDataType = {
    frontendFlags: frontendFlags ?? {},
    isAgencyOwner: false,
    isOnCustomDomain: false,
    lastAccountBasedModalViewedAt,
    isInternalUser: isDevelopFeatureEnabled(),
  };

  const announcementsToBeDisplayed = getAnnouncementsToBeDisplayed(announcements, accountData, websiteData);

  yield put(setAnnouncementData(announcementsToBeDisplayed));
  if (announcementsToBeDisplayed.length !== 0) {
    yield put(openModal(modalName.announcement));
  }
}
